import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { FORM_KEYS } from "../constants";
import {
  getConventionsSelector,
  fetchConventionProducts,
} from "../../../store/conventionsSlice";
import { getLiveConventionsSelector } from "../../../store/liveConventions";
import { selfServiceSlice, updateForm } from "../../../store/selfServiceSlice";
import { isConventionLive } from "../../../utils";
import { BRAND } from "../../../utils/constants";

/**
 * @desc Watch and update order management form.
 */
export const useWatchConvention = ({ watch, setValue, reset }) => {
  // CONSTANTS ----------------------------------------------------------------

  const dispatch = useDispatch();
  const cname = watch(FORM_KEYS.CNAME);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchCname = searchParams.get("cname");
  const targetCname = cname || searchCname;
  const conventions = useSelector(getConventionsSelector);
  const liveConventions = useSelector(getLiveConventionsSelector);
  const selectedConvention = useMemo(
    () => conventions.find(({ name }) => name === targetCname),
    [targetCname, conventions]
  );

  const isLiveFunko = isConventionLive(
    BRAND.FUNKO,
    liveConventions
  )(targetCname);
  const isLiveLoungefly = isConventionLive(
    BRAND.LOUNGEFLY,
    liveConventions
  )(targetCname);
  const isLiveMondo = isConventionLive(
    BRAND.MONDO,
    liveConventions
  )(targetCname);

  // SIDE EFFECTS -------------------------------------------------------------

  /**
   * @desc On convention change, update search params.
   */
  useEffect(() => {
    if (cname) {
      setSearchParams({ cname });
    }
  }, [cname]);

  /**
   * @desc Set form values based on live status.
   */
  useEffect(() => {
    if (selectedConvention) {
      setValue(FORM_KEYS.IS_PUBLIC_FUNKO, isLiveFunko);
      setValue(FORM_KEYS.IS_PUBLIC_LOUNGEFLY, isLiveLoungefly);
      setValue(FORM_KEYS.IS_PUBLIC_MONDO, isLiveMondo);
    }
    // eslint-disable-next-line
  }, [setValue, isLiveFunko, isLiveLoungefly, isLiveMondo]);

  /**
   * @desc Subscribe to form changes.
   */
  useEffect(() => {
    if (selectedConvention && targetCname) {
      setValue(FORM_KEYS.CNAME, selectedConvention.name || "");
      setValue(FORM_KEYS.ANNOUNCEMENT, selectedConvention.announcement || "");
      setValue(
        FORM_KEYS.BACKGROUND_COLOR,
        selectedConvention?.backgroundStyle?.backgroundColor ||
          selectedConvention?.backgroundColor ||
          "#FFFFFF"
      );
      setValue(FORM_KEYS.TITLE, selectedConvention.header || "");
      setValue(FORM_KEYS.ABBREV, selectedConvention.abbrev || "");
      setValue(FORM_KEYS.DARK_MODE, selectedConvention.darkMode || false);
      setValue(
        FORM_KEYS.BACKGROUND_IMAGE,
        selectedConvention.backgroundImage || ""
      );
      dispatch(
        updateForm({
          [FORM_KEYS.BACKGROUND_IMAGE]:
            selectedConvention.backgroundImage || "",
        })
      );
      dispatch(fetchConventionProducts(targetCname));
    } else {
      reset();
      dispatch(selfServiceSlice.actions.resetForm());
    }

    // eslint-disable-next-line
  }, [targetCname, setValue, reset, dispatch]);
};
